import { useState } from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { checkEntitlement } from "@/lib/subscription.functions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Mic, FileText, Upload } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/homework")({
  // Server-side entitlement gate: homework is a paid feature.
  beforeLoad: async () => {
    // Dynamic: beforeLoad lives in the entry bundle; keep the Stripe module out of it.
    const { getStripeEnvironment } = await import("@/lib/stripe");
    const { active } = await checkEntitlement({ data: { environment: getStripeEnvironment() } });
    if (!active) throw redirect({ to: "/dashboard" });
  },
  head: () => ({
    meta: [
      { title: "Submit homework | Smart Word Labs" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content: "Upload your child's audio recordings and worksheets for tutor review.",
      },
      { property: "og:title", content: "Submit homework | Smart Word Labs" },
      {
        property: "og:description",
        content: "Upload audio recordings and worksheets for your Smart Word Labs tutor to review.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: HomeworkPage,
});

const MAX_BYTES = 25 * 1024 * 1024;

function HomeworkPage() {
  const { t } = useTranslation("portal");
  const queryClient = useQueryClient();
  const [bookingId, setBookingId] = useState<string>("");
  const [audio, setAudio] = useState<File | null>(null);
  const [worksheet, setWorksheet] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: userId } = useQuery({
    queryKey: ["uid"],
    queryFn: async () => (await supabase.auth.getUser()).data.user?.id ?? null,
  });

  const { data: bookings } = useQuery({
    queryKey: ["my-bookings", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("id, scheduled_at, courses_and_cohorts(title)")
        .eq("student_id", userId!)
        .order("scheduled_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: submissions } = useQuery({
    queryKey: ["my-homework", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("homework_submissions")
        .select(
          "id, status, created_at, audio_file_url, worksheet_file_url, tutor_feedback_text, tutor_video_feedback_url",
        )
        .eq("student_id", userId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  async function uploadOne(file: File, kind: string) {
    if (file.size > MAX_BYTES) throw new Error(t("homework.errors.fileTooLarge", { kind }));
    const ext = file.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") || "bin";
    const path = `${userId}/${bookingId}/${kind}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("homework").upload(path, file, { upsert: false });
    if (error) throw error;
    return path;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!bookingId) {
      toast.error(t("homework.errors.chooseLesson"));
      return;
    }
    if (!audio && !worksheet) {
      toast.error(t("homework.errors.attachFile"));
      return;
    }

    setBusy(true);
    try {
      const audioPath = audio ? await uploadOne(audio, "audio") : null;
      const worksheetPath = worksheet ? await uploadOne(worksheet, "worksheet") : null;

      const { error } = await supabase.from("homework_submissions").insert({
        booking_id: bookingId,
        student_id: userId!,
        audio_file_url: audioPath,
        worksheet_file_url: worksheetPath,
      });
      if (error) throw error;

      toast.success(t("homework.toasts.submitted"));
      setAudio(null);
      setWorksheet(null);
      setBookingId("");
      queryClient.invalidateQueries({ queryKey: ["my-homework", userId] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("homework.errors.uploadFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function open(path: string) {
    const { data, error } = await supabase.storage.from("homework").createSignedUrl(path, 300);
    if (error || !data) {
      toast.error(t("homework.errors.openFileFailed"));
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      <main className="mx-auto max-w-3xl space-y-6 px-5 py-10">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/dashboard">
            <ArrowLeft /> {t("homework.backToDashboard")}
          </Link>
        </Button>

        <section className="surface-card p-7">
          <h1 className="text-2xl text-primary">{t("homework.title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("homework.description")}
          </p>

          <form className="mt-6 space-y-5" onSubmit={submit}>
            <div className="space-y-2">
              <Label>{t("homework.form.lesson")}</Label>
              <Select value={bookingId} onValueChange={setBookingId}>
                <SelectTrigger>
                  <SelectValue placeholder={t("homework.form.selectLesson")} />
                </SelectTrigger>
                <SelectContent>
                  {(bookings ?? []).map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {(b.courses_and_cohorts?.title ?? t("homework.form.lessonFallback")) +
                        " — " +
                        new Date(b.scheduled_at).toLocaleDateString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {bookings && bookings.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {t("homework.form.noBookings")}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="audio">
                <Mic className="me-1 inline size-4 text-accent" /> {t("homework.form.audioRecording")}
              </Label>
              <Input
                id="audio"
                type="file"
                accept="audio/*"
                onChange={(e) => setAudio(e.target.files?.[0] ?? null)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="worksheet">
                <FileText className="me-1 inline size-4 text-accent" /> {t("homework.form.worksheet")}
              </Label>
              <Input
                id="worksheet"
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setWorksheet(e.target.files?.[0] ?? null)}
              />
            </div>

            <Button type="submit" disabled={busy} className="w-full">
              <Upload /> {busy ? t("homework.form.uploading") : t("homework.form.submit")}
            </Button>
          </form>
        </section>

        <section className="surface-card p-7">
          <h2 className="text-xl text-primary">{t("homework.submissions.title")}</h2>
          {submissions && submissions.length > 0 ? (
            <ul className="mt-4 divide-y divide-border">
              {submissions.map((s) => (
                <li key={s.id} className="space-y-2 py-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {new Date(s.created_at).toLocaleString()}
                    </span>
                    <Badge variant="secondary">{s.status}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {s.audio_file_url && (
                      <Button variant="outline" size="sm" onClick={() => open(s.audio_file_url!)}>
                        <Mic /> {t("homework.submissions.audio")}
                      </Button>
                    )}
                    {s.worksheet_file_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => open(s.worksheet_file_url!)}
                      >
                        <FileText /> {t("homework.submissions.worksheet")}
                      </Button>
                    )}
                  </div>
                  {s.tutor_feedback_text && (
                    <p className="rounded-md bg-secondary/60 p-3 text-sm text-primary">
                      {s.tutor_feedback_text}
                    </p>
                  )}
                  {s.tutor_video_feedback_url && (
                    <a
                      className="text-sm font-semibold text-accent underline"
                      href={s.tutor_video_feedback_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("homework.submissions.watchVideoFeedback")}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">{t("homework.submissions.empty")}</p>
          )}
        </section>
      </main>
    </div>
  );
}
